import {
  DetailedLabResultObservation,
  LabResultFindingsEnum,
  LabResultPresentationTypeEnum,
} from '@pm/graphql';
import { TFunction } from 'i18next';
import invariant from 'tiny-invariant';

export const shouldUseNumericValueDisplay = (
  observation: DetailedLabResultObservation,
): boolean => observation.numericResult != null;

const RESULT_TRANSLATION_KEYS: Record<
  string,
  { clear: string; outside_range: string }
> = {
  hiv_confirmatory: {
    clear: 'ResultText.Negative',
    outside_range: 'ResultText.Positive',
  },
  urine_pregnancy: {
    clear: 'ResultText.Negative',
    outside_range: 'ResultText.Positive',
  },
  hiv_antigen_antibody: {
    clear: 'ResultText.NonReactive',
    outside_range: 'ResultText.Reactive',
  },
  syphilis_serology: {
    clear: 'ResultText.NonReactive',
    outside_range: 'ResultText.Reactive',
  },
  hepatitis_b: {
    clear: 'ResultText.NonReactive',
    outside_range: 'ResultText.Reactive',
  },
  chlamydia_urine: {
    clear: 'ResultText.NotDetected',
    outside_range: 'ResultText.Detected',
  },
  chlamydia_oral: {
    clear: 'ResultText.NotDetected',
    outside_range: 'ResultText.Detected',
  },
  chlamydia_rectal: {
    clear: 'ResultText.NotDetected',
    outside_range: 'ResultText.Detected',
  },
  gonorrhea_urine: {
    clear: 'ResultText.NotDetected',
    outside_range: 'ResultText.Detected',
  },
  gonorrhea_oral: {
    clear: 'ResultText.NotDetected',
    outside_range: 'ResultText.Detected',
  },
  gonorrhea_rectal: {
    clear: 'ResultText.NotDetected',
    outside_range: 'ResultText.Detected',
  },
  lipid_panel: {
    clear: 'ResultText.Normal',
    outside_range: 'ResultText.Abnormal',
  },
  creatinine_six_months: {
    clear: 'ResultText.Normal',
    outside_range: 'ResultText.Abnormal',
  },
};

const getResultText = (
  internalName: string | undefined,
  findings: string | null | undefined,
  t: TFunction<'labs'>,
  fallback: string,
): string => {
  if (!internalName || !findings) return fallback;
  const keys = RESULT_TRANSLATION_KEYS[internalName];
  if (!keys) return fallback;
  const key = keys[findings as keyof typeof keys];
  return key ? (t(key as never) as string) : fallback;
};

export const getResultTextForSection = (
  internalName: string | undefined,
  findings: string | null | undefined,
  t: TFunction<'labs'>,
): string => getResultText(internalName, findings, t, t('Unknown'));

// toPrecision(2) gives 2 significant figures but produces "1.1e+2" for large numbers;
// parseFloat converts that back to a plain number string ("110").
const formatSigFigs = (n: number): string =>
  String(parseFloat(n.toPrecision(2)));

// builds result string with unit (i.e. 5.4 mmol/L) and drops trailing spaces
export const formatNumericValue = (
  observation: DetailedLabResultObservation,
): string =>
  [
    observation.numericResult != null
      ? formatSigFigs(observation.numericResult)
      : undefined,
    observation.unit,
  ]
    .filter(Boolean)
    .join(' ');

export const formatNumericReferenceRange = (
  observation: DetailedLabResultObservation,
): string => {
  invariant(
    observation.rangeLow != null && observation.rangeHigh != null,
    'formatNumericReferenceRange requires both rangeLow and rangeHigh',
  );
  const rangeStr = `${formatSigFigs(observation.rangeLow)} - ${formatSigFigs(observation.rangeHigh)}`;
  return [rangeStr, observation.unit].filter(Boolean).join(' ');
};

// Returns a displayable reference range string, falling back to the raw
// referenceRange string (+ unit) when bounds aren't available (e.g. quantity not sufficient)
export const formatReferenceRangeDisplay = (
  observation: DetailedLabResultObservation,
): string | null => {
  if (observation.rangeLow != null && observation.rangeHigh != null) {
    return formatNumericReferenceRange(observation);
  }
  if (observation.referenceRange) {
    return [observation.referenceRange, observation.unit]
      .filter(Boolean)
      .join(' ');
  }
  return null;
};

const RESULT_CODE_LABELS: Record<string, string> = {
  QNS: 'Quantity Not Sufficient',
};

// Result codes the backend cannot classify against a reference range —
// always treat as OutsideRange so the UI shows the yellow/warning style.
const ATTENTION_RESULT_CODES = new Set(['QNS']);

export const resolveFindings = (
  observation: DetailedLabResultObservation,
): {
  findings: string | null | undefined;
  presentationType: LabResultPresentationTypeEnum | null | undefined;
} => {
  if (ATTENTION_RESULT_CODES.has(observation.result ?? '')) {
    return {
      findings: LabResultFindingsEnum.OutsideRange,
      presentationType: LabResultPresentationTypeEnum.Attention,
    };
  }
  return {
    findings: observation.findings,
    presentationType: observation.presentationType,
  };
};

export const formatValue = (
  observation: DetailedLabResultObservation,
): string | null | undefined => {
  if (shouldUseNumericValueDisplay(observation)) {
    return formatNumericValue(observation);
  }
  const result = observation.result ?? '';
  return RESULT_CODE_LABELS[result] ?? observation.result;
};

const RESULT_STYLES = {
  clear: {
    attention: {
      color: 'contentPositive',
      iconColor: 'text-content-positive',
      icon: 'check_circle',
    },
    neutral: {
      color: 'contentDefault',
      iconColor: 'text-content-subdued',
      icon: 'info',
    },
  },
  notClear: {
    attention: {
      color: 'contentWarning',
      iconColor: 'text-content-warning',
      icon: 'warning',
    },
    neutral: {
      color: 'contentDefault',
      iconColor: 'text-content-subdued',
      icon: 'info',
    },
  },
} as const;

export const getResultStyle = (
  findings: string | null | undefined,
  presentationType: LabResultPresentationTypeEnum | null | undefined,
) => {
  const findingsKey =
    findings === LabResultFindingsEnum.Clear ? 'clear' : 'notClear';
  const presentationKey =
    presentationType ?? LabResultPresentationTypeEnum.Neutral;
  return RESULT_STYLES[findingsKey][presentationKey];
};

export const hasFlaggedObservations = (
  observations: DetailedLabResultObservation[],
) =>
  observations.some(
    ({ findings }) => findings === LabResultFindingsEnum.OutsideRange,
  );
